/**
 * Bidirectional sync between the useFilters store (filters/page/pageSize/sort) and
 * the URL search params (?f=<base64 json>&page=&size=&sort=). Mount ONCE in BasePage.
 *
 * On mount: read the URL and hydrate the store (so reload / shared links restore the
 * Base view). After that: subscribe to the store and mirror changes back into the URL
 * via history.replaceState (no navigation, no history spam).
 *
 * `f` is base64(JSON(filters[])) — compact and resilient to commas/quotes in values.
 */

import * as React from "react";

import type { FilterClause } from "@/api/client";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, useFilters } from "@/store/filters";

/** base64-encode a JSON-able value (UTF-8 safe). */
function encodeFilters(filters: FilterClause[]): string {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(filters))));
  } catch {
    return "";
  }
}

const VALID_OPERATORS: ReadonlySet<string> = new Set([
  "eq", "ieq", "contains", "includes", "starts", "ends", "exists", "is_empty",
  "among", "bday", "gte", "lte", "from", "to", "between", "between_numeric", "child_of", "in",
]);
/** Operators that legitimately carry no value (presence tests). */
const VALUELESS_OPERATORS: ReadonlySet<string> = new Set(["exists", "is_empty"]);

/**
 * Inverse of encodeFilters; returns null on any decode/shape error. Drops any
 * malformed clause (missing field/operator, unknown operator, or missing value for
 * a value-bearing operator) rather than passing it upstream, so a corrupt `?f=`
 * link degrades to a partial/empty filter instead of a sticky 422 (F47).
 */
function decodeFilters(raw: string): FilterClause[] | null {
  try {
    const json = decodeURIComponent(escape(atob(raw)));
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((c): c is FilterClause => {
      if (!c || typeof c !== "object") return false;
      const cl = c as Record<string, unknown>;
      if (typeof cl.field !== "string" || cl.field === "") return false;
      if (typeof cl.operator !== "string" || !VALID_OPERATORS.has(cl.operator)) return false;
      if (!VALUELESS_OPERATORS.has(cl.operator) && (cl.value === undefined || cl.value === null)) {
        return false;
      }
      return true;
    });
  } catch {
    return null;
  }
}

/** Build the search string the current store state should produce. */
function buildSearch(state: {
  filters: FilterClause[];
  page: number;
  pageSize: number;
  sort: string | null;
}): string {
  const params = new URLSearchParams();
  if (state.filters.length > 0) params.set("f", encodeFilters(state.filters));
  if (state.page > 1) params.set("page", String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) params.set("size", String(state.pageSize));
  if (state.sort) params.set("sort", state.sort);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useUrlFilters(): void {
  // Read URL -> store once on mount.
  const hydrated = React.useRef(false);
  if (!hydrated.current) {
    hydrated.current = true;
    const params = new URLSearchParams(window.location.search);

    const f = params.get("f");
    // Only hydrate from the URL when it actually carries view state. Navigating to
    // a bare "/" via the «Активисты» NavLink (no params) must NOT wipe the applied
    // filters that live in the store — otherwise returning to the Base clears them
    // (F18). An explicit "Сбросить" empties the store the honest way.
    const carriesState =
      f != null || params.has("page") || params.has("size") || params.has("sort");
    if (carriesState) {
      const filters = f ? decodeFilters(f) : null;

      const pageRaw = Number(params.get("page"));
      const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1;

      const sizeRaw = Number(params.get("size"));
      const pageSize = (PAGE_SIZE_OPTIONS as readonly number[]).includes(sizeRaw)
        ? sizeRaw
        : DEFAULT_PAGE_SIZE;

      const sort = params.get("sort");

      // Apply atomically so we don't trigger several store->URL writes mid-hydration.
      useFilters.setState({
        filters: filters ?? [],
        page,
        pageSize,
        sort: sort ?? null,
      });
    }
  }

  // Subscribe store -> URL (replaceState).
  React.useEffect(() => {
    const sync = () => {
      const { filters, page, pageSize, sort } = useFilters.getState();
      const search = buildSearch({ filters, page, pageSize, sort });
      const url = `${window.location.pathname}${search}${window.location.hash}`;
      if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        window.history.replaceState(window.history.state, "", url);
      }
    };
    // Push the hydrated state into the URL immediately, then on every change.
    sync();
    return useFilters.subscribe(sync);
  }, []);
}
